import {
  BATCH_GATES,
  type Ask,
  type GridRow,
} from '@/james-playground/aa-family-claim/claim/asks';
import {
  isEmpty,
  text,
  type Facts,
  type Row,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import {
  applies,
  signerOf,
  statusOf,
} from '@/james-playground/aa-family-claim/claim/engine';
import {
  entry,
  type Condition,
} from '@/james-playground/aa-family-claim/claim/registry';

/**
 * The pure half of the answer cards: what a card starts from, what it shows,
 * and the values it hands back. Everything here follows the registry's
 * conventions, so the PDF reads a card's answer the same way it reads one
 * proposed from a voice note:
 *
 * - yes_no and single_choice take the option label ('Yes', 'No', 'Hospital').
 * - multi_choice is a list of option labels.
 * - a table is a list of rows keyed by column id.
 * - a date is DD/MM/YYYY; a rough date is "March 2025" or "2025".
 * - a count is a numeric string.
 * - an empty string is an answer meaning "none".
 */

export type Draft = Record<string, Value>;

// ---- What a card starts from ------------------------------------------------

/** What the claim already holds for an entry, proposed or confirmed. */
export const known = (facts: Facts, id: string): Value | undefined => {
  const fact = facts[id];
  if (!fact || (fact.state !== 'proposed' && fact.state !== 'confirmed'))
    return undefined;
  return fact.value;
};

/** A card's first state: everything the claim already has for its entries. */
export const prefill = (ids: string[], facts: Facts): Draft =>
  Object.fromEntries(
    ids.flatMap((id) => {
      const value = known(facts, id);
      return value === undefined ? [] : [[id, value]];
    }),
  );

export const str = (value: Value | undefined): string =>
  typeof value === 'string' ? value : '';

export const list = (value: Value | undefined): string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')
    ? (value as string[])
    : typeof value === 'string' && value
      ? [value]
      : [];

export const rows = (value: Value | undefined): Row[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'object')
    ? (value as Row[])
    : [];

// ---- What a card shows ------------------------------------------------------

/**
 * The facts as they would be if the card's answers so far were given, so a
 * follow-up appears the moment its gate is answered inside the card. A
 * cleared answer counts as unknown.
 */
export const withDraft = (facts: Facts, draft: Draft): Facts => {
  const live: Facts = { ...facts };
  for (const [id, value] of Object.entries(draft)) {
    if (isEmpty(value)) delete live[id];
    else live[id] = { value, state: 'confirmed', source: 'family' };
  }
  return live;
};

const refs = (c?: Condition): string[] =>
  !c
    ? []
    : 'all' in c
      ? c.all.flatMap(refs)
      : 'any' in c
        ? c.any.flatMap(refs)
        : 'id' in c
          ? [c.id]
          : [];

/** The entries an entry's condition depends on. */
export const decidedBy = (id: string): string[] => refs(entry(id).when);

/**
 * Whether a card shows an entry. It shows when the form asks it, given what
 * the card has so far. One still waiting on an answer inside the card stays
 * hidden until that answer is given; one waiting on something outside the card
 * shows, since nothing here can settle it.
 */
export const shows = (
  id: string,
  inCard: string[],
  facts: Facts,
  draft: Draft,
): boolean => {
  const e = entry(id);
  const decided = applies(e, withDraft(facts, draft));
  if (decided !== undefined) return decided;
  return !refs(e.when).some((r) => inCard.includes(r));
};

/** The values a card hands back: its answers for the entries it shows. */
export const shownValues = (
  ids: string[],
  inCard: string[],
  facts: Facts,
  draft: Draft,
  { keepEmpty = false }: { keepEmpty?: boolean } = {},
): Record<string, Value> =>
  Object.fromEntries(
    ids
      .filter((id) => shows(id, inCard, facts, draft))
      .filter((id) => id in draft && (keepEmpty || !isEmpty(draft[id])))
      .map((id) => [
        id,
        typeof draft[id] === 'string'
          ? (draft[id] as string).trim()
          : draft[id],
      ]),
  );

// ---- Numbers ------------------------------------------------------------------

const WORD_COUNTS: Record<string, number> = {
  once: 1,
  one: 1,
  twice: 2,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

/** "2", "Twice", "Once a day" and "3 times" all read as a count. */
export const countOf = (value: Value | undefined): number | undefined => {
  if (typeof value !== 'string') return undefined;
  const digits = value.match(/\d+/);
  if (digits) return Number(digits[0]);
  const word = value
    .toLowerCase()
    .match(/\b(once|twice|one|two|three|four|five|six|seven|eight|nine|ten)\b/);
  return word ? WORD_COUNTS[word[1]] : undefined;
};

export const digitsOnly = (value: string) => value.replace(/\D/g, '');

// ---- Help grids ------------------------------------------------------------

export type HelpKind = 'difficulty' | 'help' | 'prompt';
export const HELP_KINDS: HelpKind[] = ['difficulty', 'help', 'prompt'];

/** One cell of a help grid: switched on, and for a daily count, how many. */
export type Cell = { on: boolean; count: string };
export type Cells = Record<string, Cell>;

export const cellIds = (gridRows: GridRow[]): string[] =>
  gridRows.flatMap((row) =>
    HELP_KINDS.map((kind) => row[kind]).filter((id): id is string =>
      Boolean(id),
    ),
  );

export const initialCells = (ask: Ask, facts: Facts): Cells =>
  Object.fromEntries(
    cellIds(ask.rows ?? []).map((id) => {
      const value = known(facts, id);
      if (ask.unit === 'perDay') {
        const n = countOf(value) ?? 0;
        return [id, { on: n > 0, count: n > 0 ? String(n) : '' }];
      }
      return [id, { on: value === 'Yes' || value === 'Ticked', count: '' }];
    }),
  );

/** Every switched-on daily cell has a count of one or more. */
export const gridReady = (ask: Ask, gate: string, cells: Cells): boolean => {
  if (gate === 'No') return true;
  if (gate !== 'Yes') return false;
  const on = cellIds(ask.rows ?? []).filter((id) => cells[id]?.on);
  if (!on.length) return false;
  return (
    ask.unit !== 'perDay' ||
    on.every((id) => (countOf(cells[id].count) ?? 0) > 0)
  );
};

/**
 * A grid's answer. The gate, then every cell the form has for that activity:
 * a tick is Yes or No, and a daily count is the number, or an empty string for
 * none. A No on the gate answers the gate alone; the cells no longer apply.
 */
export const gridValues = (
  ask: Ask,
  gate: string,
  cells: Cells,
): Record<string, Value> => {
  const out: Record<string, Value> = {};
  if (ask.gate) out[ask.gate] = gate;
  if (gate !== 'Yes') return out;
  for (const id of cellIds(ask.rows ?? [])) {
    const cell = cells[id];
    if (ask.unit === 'perDay') {
      const n = cell?.on ? countOf(cell.count) : undefined;
      out[id] = n ? String(n) : '';
    } else out[id] = cell?.on ? 'Yes' : 'No';
  }
  return out;
};

// ---- Night grid -------------------------------------------------------------

export type NightCell = { on: boolean; times: string; minutes: string };
/** Keyed by each night row's "times" entry. */
export type NightCells = Record<string, NightCell>;

export const initialNight = (ask: Ask, facts: Facts): NightCells =>
  Object.fromEntries(
    (ask.nightRows ?? []).map((row) => {
      const times = str(known(facts, row.times));
      const minutes = str(known(facts, row.minutes));
      return [
        row.times,
        { on: Boolean(times), times, minutes: digitsOnly(minutes) },
      ];
    }),
  );

export const nightReady = (
  ask: Ask,
  gate: string,
  cells: NightCells,
): boolean => {
  if (gate === 'No') return true;
  if (gate !== 'Yes') return false;
  const on = (ask.nightRows ?? []).filter((row) => cells[row.times]?.on);
  return (
    on.length > 0 &&
    on.every(
      (row) => cells[row.times].times && Number(cells[row.times].minutes) > 0,
    )
  );
};

/**
 * The night grid's answer: for a row switched on, how many times (the form's
 * own 1, 2 or 3+) and the minutes each time; for a row left off, none.
 */
export const nightValues = (
  ask: Ask,
  gate: string,
  cells: NightCells,
): Record<string, Value> => {
  const out: Record<string, Value> = {};
  if (ask.gate) out[ask.gate] = gate;
  if (gate !== 'Yes') return out;
  for (const row of ask.nightRows ?? []) {
    const cell = cells[row.times];
    out[row.times] = cell?.on ? cell.times : '';
    out[row.minutes] = cell?.on ? digitsOnly(cell.minutes) : '';
  }
  return out;
};

// ---- Batch check --------------------------------------------------------------

/** The activity gates nobody has answered yet, in the batch check's order. */
export const openGates = (facts: Facts) =>
  BATCH_GATES.filter((g) => statusOf(entry(g.id), facts) === 'open');

/**
 * Ticked means she has difficulty or needs help (falls: a fall); unticked is
 * the family confirming she manages on her own (falls: no).
 */
export const batchValues = (
  gates: string[],
  ticked: string[],
): Record<string, Value> =>
  Object.fromEntries(
    gates.map((id) => {
      const on = ticked.includes(id);
      return [
        id,
        id === 'fallsOrStumbles' ? [on ? 'Fall' : 'No'] : on ? 'Yes' : 'No',
      ];
    }),
  );

// ---- Tables --------------------------------------------------------------------

/** A table's columns, in the form's order. Q39's are its first row of cells. */
export const columnIds = (tableId: string): string[] =>
  entry(tableId).table?.columnIds ?? [];

/** Rows as the form takes them: trimmed, keyed by column id, blank rows dropped. */
export const cleanRows = (tableRows: Row[], columns: string[]): Row[] =>
  tableRows
    .map((row) =>
      Object.fromEntries(
        columns.flatMap((c) => {
          const v = (row[c] ?? '').trim();
          return v ? [[c, v]] : [];
        }),
      ),
    )
    .filter((row) => Object.keys(row).length > 0);

// ---- Dates ---------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');

export const dateText = (d: Date) =>
  `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

/** A typed date as DD/MM/YYYY, or undefined when it is not a real date. */
export const normaliseDate = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  const parts = trimmed.split(/[/.\-\s]+/).filter(Boolean);
  let d: number;
  let m: number;
  let y: number;
  if (parts.length === 3) [d, m, y] = parts.map(Number);
  else if (/^\d{8}$/.test(trimmed))
    [d, m, y] = [
      trimmed.slice(0, 2),
      trimmed.slice(2, 4),
      trimmed.slice(4),
    ].map(Number);
  else return undefined;
  if (!Number.isInteger(d) || !Number.isInteger(m) || !Number.isInteger(y))
    return undefined;
  if (y < 1900 || y > 2100) return undefined;
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  )
    return undefined;
  return dateText(date);
};

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** A rough date: a year, and the month if they know it. */
export type Approx = {
  month: number | null;
  year: number | null;
  noMonth: boolean;
};

export const approxText = (a: Approx): string =>
  a.year === null
    ? ''
    : a.noMonth || a.month === null
      ? String(a.year)
      : `${MONTHS[a.month - 1]} ${a.year}`;

export const approxReady = (a: Approx) =>
  a.year !== null && (a.noMonth || a.month !== null);

/** "March 2025", "Mar 2025", "2025", "19/03/2025" or "2025-03-19". */
export const parseApprox = (value: Value | undefined): Approx => {
  const v = str(value).trim();
  const exact = normaliseDate(v);
  if (exact) {
    const [, m, y] = exact.split('/').map(Number);
    return { month: m, year: y, noMonth: false };
  }
  const iso = v.match(/^(\d{4})-(\d{2})/);
  if (iso)
    return { month: Number(iso[2]), year: Number(iso[1]), noMonth: false };
  const year = v.match(/\b(19|20)\d{2}\b/);
  if (!year) return { month: null, year: null, noMonth: false };
  const month = MONTHS.findIndex((name) =>
    new RegExp(`\\b${name.slice(0, 3)}`, 'i').test(v),
  );
  return month >= 0
    ? { month: month + 1, year: Number(year[0]), noMonth: false }
    : { month: null, year: Number(year[0]), noMonth: true };
};

/** Six months after a rough start date: when payment could begin (rule R5.1). */
export const qualifiesFrom = (a: Approx): Date | undefined => {
  if (a.year === null) return undefined;
  const month = a.noMonth || a.month === null ? 0 : a.month - 1;
  return new Date(a.year, month + 6, 1);
};

// ---- Typed formats ---------------------------------------------------------

/** QQ 12 34 56 C as the form wants it: nine characters, no spaces. */
export const normaliseNi = (raw: string) =>
  raw.replace(/\s+/g, '').toUpperCase();
export const isNi = (raw: string) =>
  /^[A-Z]{2}\d{6}[A-D]$/.test(normaliseNi(raw));
export const formatNi = (raw: string) => {
  const ni = normaliseNi(raw);
  return isNi(ni)
    ? `${ni.slice(0, 2)} ${ni.slice(2, 4)} ${ni.slice(4, 6)} ${ni.slice(6, 8)} ${ni.slice(8)}`
    : raw;
};

/** A UK number has 10 or 11 digits; +44 counts as the leading 0. */
export const isPhone = (raw: string) => {
  const d = digitsOnly(raw.replace(/^\s*\+44/, '0'));
  return d.length >= 10 && d.length <= 11;
};

export const formatSortCode = (raw: string) =>
  digitsOnly(raw)
    .slice(0, 6)
    .replace(/(\d{2})(?=\d)/g, '$1-');
export const isSortCode = (raw: string) => digitsOnly(raw).length === 6;
export const isAccountNumber = (raw: string) => {
  const n = digitsOnly(raw).length;
  return n >= 6 && n <= 10;
};

/** The format problem with a typed value, keyed for the card's copy. */
export type FormatProblem = 'date' | 'ni' | 'phone';

export const formatProblem = (
  answerType: string,
  raw: string,
): FormatProblem | undefined => {
  if (!raw.trim()) return undefined;
  if (answerType === 'date') return normaliseDate(raw) ? undefined : 'date';
  if (answerType === 'ni_number') return isNi(raw) ? undefined : 'ni';
  if (answerType === 'phone') return isPhone(raw) ? undefined : 'phone';
  return undefined;
};

/** A typed value as the form stores it. */
export const storedAs = (answerType: string, raw: string): string => {
  if (answerType === 'date') return normaliseDate(raw) ?? raw.trim();
  if (answerType === 'ni_number')
    return isNi(raw) ? normaliseNi(raw) : raw.trim();
  return raw.trim();
};

// ---- Confirm ---------------------------------------------------------------

/** Entries where nothing is a fair answer: she has no other names, no title. */
export const CAN_BE_NONE = new Set(['otherNames', 'title']);

/** A value the claim can work out when nobody has said it yet. */
export const suggestion = (id: string, facts: Facts): string | undefined => {
  if (id === 'declarationPrintedName') {
    if (text(facts, 'signingForSomeoneElse') === 'Yes')
      return text(facts, 'representativeName') || undefined;
    const name = [text(facts, 'firstNames'), text(facts, 'surname')]
      .filter(Boolean)
      .join(' ');
    return name || undefined;
  }
  return undefined;
};

// ---- Falls ---------------------------------------------------------------

export type FallsPick = 'Fall' | 'Stumble' | 'No';

/** Fall and Stumble go together; No stands alone. */
export const toggleFalls = (current: string[], pick: FallsPick): string[] => {
  if (pick === 'No') return current.includes('No') ? [] : ['No'];
  const without = current.filter((p) => p !== 'No');
  return without.includes(pick)
    ? without.filter((p) => p !== pick)
    : [...without, pick];
};

// ---- Signer ---------------------------------------------------------------

export type CategoryKey =
  | 'quicker'
  | 'appointee'
  | 'attorney'
  | 'deputy'
  | 'tutor'
  | 'curator'
  | 'organisation'
  | 'applying';

const CATEGORY_MATCH: Record<CategoryKey, RegExp> = {
  quicker: /special rules/i,
  appointee: /appointee, appointed/i,
  attorney: /Power of Attorney/i,
  deputy: /Deputy/i,
  tutor: /Tutor/i,
  curator: /curator bonis/i,
  organisation: /Corporate/i,
  applying: /want to be appointed/i,
};

/** The registry's own wording for a Q15 category, which the PDF ticks by. */
export const categoryLabel = (key: CategoryKey): string => {
  const option = entry('signerCategory').options?.find((o) =>
    CATEGORY_MATCH[key].test(o.label),
  );
  if (!option) throw new Error(`No Q15 category for "${key}"`);
  return option.label;
};

export type Who = 'herself' | 'quicker' | 'attorney' | 'authority' | 'unsure';
export type PoaType = 'finance' | 'welfare' | 'unregistered';
export type Authority =
  'appointee' | 'deputy' | 'tutor' | 'curator' | 'organisation';
export type Unsure = 'herself' | 'apply' | 'adviser';
export type SignerState = {
  who?: Who;
  poa?: PoaType;
  authority?: Authority;
  unsure?: Unsure;
};

export type SignerOutcome =
  { someoneElse: 'Yes' | 'No'; category?: CategoryKey } | 'adviser' | undefined;

const fromUnsure = (u?: Unsure): SignerOutcome =>
  u === 'herself'
    ? { someoneElse: 'No' }
    : u === 'apply'
      ? { someoneElse: 'Yes', category: 'applying' }
      : u === 'adviser'
        ? 'adviser'
        : undefined;

/** A Health and Welfare or unregistered attorney cannot sign: it goes as "not sure". */
export const goesAsUnsure = (s: SignerState) =>
  s.who === 'unsure' ||
  (s.who === 'attorney' && (s.poa === 'welfare' || s.poa === 'unregistered'));

export const signerOutcome = (s: SignerState): SignerOutcome => {
  if (goesAsUnsure(s)) return fromUnsure(s.unsure);
  switch (s.who) {
    case 'herself':
      return { someoneElse: 'No' };
    case 'quicker':
      return { someoneElse: 'Yes', category: 'quicker' };
    case 'attorney':
      return s.poa === 'finance'
        ? { someoneElse: 'Yes', category: 'attorney' }
        : undefined;
    case 'authority':
      return s.authority
        ? { someoneElse: 'Yes', category: s.authority }
        : undefined;
    default:
      return undefined;
  }
};

/** Where the signer flow starts from what the claim already knows. */
export const signerStateFrom = (facts: Facts): SignerState => {
  const someoneElse = str(known(facts, 'signingForSomeoneElse'));
  if (someoneElse === 'No') return { who: 'herself' };
  if (someoneElse !== 'Yes') return {};
  const category = str(known(facts, 'signerCategory'));
  const key = (Object.keys(CATEGORY_MATCH) as CategoryKey[]).find((k) =>
    CATEGORY_MATCH[k].test(category),
  );
  switch (key) {
    case 'quicker':
      return { who: 'quicker' };
    case 'attorney':
      return { who: 'attorney', poa: 'finance' };
    case 'applying':
      return { who: 'unsure', unsure: 'apply' };
    case undefined:
      return {};
    default:
      return { who: 'authority', authority: key };
  }
};

/** The Q15 answers for an outcome, before the signer's own details. */
export const signerValues = (
  outcome: Exclude<SignerOutcome, 'adviser' | undefined>,
): Record<string, Value> => ({
  signingForSomeoneElse: outcome.someoneElse,
  ...(outcome.category
    ? { signerCategory: categoryLabel(outcome.category) }
    : {}),
});

// ---- Hospital and care stays ------------------------------------------------

export type StayLine =
  | 'hospital'
  | 'publicCareHome'
  | 'publicHospice'
  | 'canBePaid'
  | 'hospiceQuicker';

/** What a stay now means for payment (spec 6), once enough is known to say. */
export const stayLine = (
  placeType: string,
  publiclyFunded: string,
  specialRules: boolean,
): StayLine | undefined => {
  if (placeType === 'Hospital') return 'hospital';
  if (placeType === 'Hospice' && specialRules) return 'hospiceQuicker';
  if (!publiclyFunded) return undefined;
  if (publiclyFunded === 'No') return 'canBePaid';
  return placeType === 'Hospice' ? 'publicHospice' : 'publicCareHome';
};

// ---- Documents ---------------------------------------------------------------

/** The documents already listed for Q65, one per line. */
export const listedDocuments = (facts: Facts): string[] =>
  str(known(facts, 'attachments'))
    .split(/\n|;\s*/)
    .map((d) => d.trim())
    .filter(Boolean);

/** Documents the signer's route needs, which the family may not have uploaded. */
export const routeDocuments = (
  facts: Facts,
): ('poa' | 'deputy' | 'scottish' | 'organisation' | 'appointee' | 'sr1')[] => {
  const out: (
    'poa' | 'deputy' | 'scottish' | 'organisation' | 'appointee' | 'sr1'
  )[] = [];
  const signer = signerOf(facts);
  if (signer === 'attorney' && text(facts, 'poaOnlineAccount') !== 'Yes')
    out.push('poa');
  if (signer === 'deputy') out.push('deputy');
  if (signer === 'scottish') out.push('scottish');
  if (signer === 'organisation') out.push('organisation');
  if (signer === 'appointee') out.push('appointee');
  if (text(facts, 'specialRules') === 'Yes') out.push('sr1');
  return out;
};
